import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { ApiError, ApiProblemDetails } from '../../shared/models/problem-details';
import { AuthStore } from '../auth/auth-store';

/**
 * Normalizes every failed HTTP response into an {@link ApiError} built from `ASB.API`'s
 * `application/problem+json` body, so components never parse `HttpErrorResponse` themselves.
 * Also logs the user out and redirects to `/login` on a 401, since that means the session's
 * token is missing, expired, or invalid.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      if (error.status === 401 && authStore.isAuthenticated()) {
        authStore.clear();
        void router.navigate(['/login']);
      }

      const problem = isProblemDetails(error.error) ? error.error : undefined;
      const retryAfterHeader = error.headers.get('Retry-After');

      const apiError: ApiError = {
        status: error.status,
        title: problem?.title ?? 'Unerwarteter Fehler',
        detail: problem?.detail,
        errors: problem?.errors,
        lockoutEndUtc: problem?.lockoutEndUtc,
        retryAfterSeconds: retryAfterHeader ? Number(retryAfterHeader) : undefined,
      };

      return throwError(() => apiError);
    }),
  );
};

function isProblemDetails(value: unknown): value is ApiProblemDetails {
  return typeof value === 'object' && value !== null;
}
